import { Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import GS from '../../grafsync/grafsync';
import DownloadOptionButton from '../DownloadOptionButton';
import LabelledSwitch from '../LabelledSwitch';
import LoadingButton from '../LoadingButton';
import SecondaryButton from '../SecondaryButton';
import { globalStyles } from '../style/gstheme';
import { sortByProp } from '../utils/utils';
import RenameTemplateDialog from './RenameTemplateDialog';
import TSVoptions from './TSVoptions';
import TSVoptionSeparator from './TSVoptionSeparator';
import TSVselectedOption from './TSVselectedOption';

const styles = () => {
    return Object.assign({
        columns: {
            clear: 'both',
            height: 180,
            minWidth: '100%'
        },
        selectionPanelContainer: {
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
            clear: 'both',
            padding: 12,
            backgroundColor: '#F5F5F5',
        },
        selectionPanel: {
            height: 220,
            overflowX: 'scroll'
        },
        actions: {
            lear: 'both',
            backgroundColor: '#eeeeee',
            overflow: 'auto',
            padding: 12,
            borderBottomLeftRadius: 4,
            borderBottomRightRadius: 4,
        }
    }, globalStyles);
};

class TSVbuilder extends React.Component {

    constructor(props) {
        super(props);
        const startingTemplate = Object.assign({}, props.template);
        delete startingTemplate.tsvTemplateOption;
        this.state = {
            draggingIndex: null,
            nameOpen: false,
            template: startingTemplate,
            referencesAsLabels:  props.template.tsvTemplateOption && props.template.tsvTemplateOption.includes("https://grafsync.graphifi.com/schema/tsv/referencesAsLabels"),
            includeHeaderRow: props.template.tsvTemplateOption && props.template.tsvTemplateOption.includes("https://grafsync.graphifi.com/schema/tsv/includeheader"),
            useCustomSorting: props.examples && props.examples.length > 0
                && props.options.sortProperty && props.template.tsvTemplateOption
                && props.template.tsvTemplateOption.includes("https://grafsync.graphifi.com/schema/tsv/useCustomSorting")
        };
        this.addRef = React.createRef();
    }

    updateState(property, value) {
        let newTemplate = Object.assign({}, this.state);
        if (newTemplate[property]) {
            Array.isArray(newTemplate[property]) ?
                newTemplate[property].push(value)
                :
                newTemplate[property] = [...newTemplate[property], value];
        }
        else {
            newTemplate[property] = [value];
        }
        this.setState({ template: newTemplate });
    }

    handleHeaderRowChange = checked => this.setState({ includeHeaderRow: checked });

    handleReferencesAsLabelsChange = checked => this.setState({ referencesAsLabels: checked });

    handleCustomSortingChange = checked => this.setState({ useCustomSorting: checked });

    removeOption = (index) => {
        let newTemplate = Object.assign({}, this.state.template);
        newTemplate.tsvColumnProperty.splice(index, 1);
        newTemplate.tsvColumnLanguage.splice(index, 1);
        this.setState({ template: newTemplate });
    };

    moveOption = (sourceIndex, targetIndex) => {
        let newTemplate = Object.assign({}, this.state.template);
        const sourceProp = newTemplate.tsvColumnProperty[sourceIndex];
        const sourceLang = newTemplate.tsvColumnLanguage[sourceIndex];
        newTemplate.tsvColumnProperty.splice(sourceIndex, 1);
        newTemplate.tsvColumnLanguage.splice(sourceIndex, 1);
        newTemplate.tsvColumnProperty.splice(targetIndex, 0, sourceProp);
        newTemplate.tsvColumnLanguage.splice(targetIndex, 0, sourceLang);
        this.setState({ template: newTemplate });
    };

    onCreate = (format) => {
        let options = [];
        if (this.state.includeHeaderRow) options.push("https://grafsync.graphifi.com/schema/tsv/includeheader");
        if (this.state.referencesAsLabels) options.push("https://grafsync.graphifi.com/schema/tsv/referencesAsLabels");
        if (this.state.useCustomSorting) options.push("https://grafsync.graphifi.com/schema/tsv/useCustomSorting");
        this.props.onCreate(
            Object.assign({}, this.state.template, {
                ...(options.length > 0) && { tsvTemplateOption: options }
            }),
            format
        );
    };

    promptForName = () => this.setState({ nameOpen: true });

    exitName = () => this.setState({ nameOpen: false });

    save = (title) => {
        this.setState({ nameOpen: false }, async () => {
            const templateIRI = "https://graphologi.com/data/template/" + uuidv4();
            let options = [];
            if (this.state.includeHeaderRow) options.push("https://grafsync.graphifi.com/schema/tsv/includeheader");
            if (this.state.referencesAsLabels) options.push("https://grafsync.graphifi.com/schema/tsv/referencesAsLabels");
            if (this.state.useCustomSorting) options.push("https://grafsync.graphifi.com/schema/tsv/useCustomSorting");
            let newTemplate = Object.assign({}, this.state.template, {
                title: { [this.props.defaultLang]: title },
                tsvTemplateTarget: this.props.templateTarget,
                ...(options.length > 0) && { tsvTemplateOption: options },
                id: templateIRI
            });
            delete newTemplate.inNamedGraph;
            delete newTemplate.created;
            await GS.insertResource(newTemplate, templateIRI, this.props.graph);
        });
    };

    addColumn = (property, lang) => {
        let newTemplate = Object.assign({}, this.state.template);
        if (!newTemplate.tsvColumnProperty) newTemplate.tsvColumnProperty = [];
        if (!newTemplate.tsvColumnLanguage) newTemplate.tsvColumnLanguage = [];
        newTemplate.tsvColumnProperty.push(property);
        const columnLang = lang || "und";
        newTemplate.tsvColumnLanguage.push(columnLang);
        this.setState({ template: newTemplate });
    };

    swapColumns = (index) => {
        let newTemplate = Object.assign({}, this.state.template);
        const leftSideProp = newTemplate.tsvColumnProperty[index];
        const rightSideProp = newTemplate.tsvColumnProperty[index + 1];
        const leftSideLang = newTemplate.tsvColumnLanguage[index];
        const rightSideLang = newTemplate.tsvColumnLanguage[index + 1];
        newTemplate.tsvColumnProperty[index] = rightSideProp;
        newTemplate.tsvColumnProperty[index + 1] = leftSideProp;
        newTemplate.tsvColumnLanguage[index] = rightSideLang;
        newTemplate.tsvColumnLanguage[index + 1] = leftSideLang;
        this.setState({ template: newTemplate });
    };

    beginDragging = (draggingIndex) => this.setState({ draggingIndex });

    endDragging = () => this.setState({ draggingIndex: null });

    render() {

        const { classes, allowSave, onDiscard, defaultLang, options, loadInfo, examples, graph, isProcessing } = this.props;
        const { template, includeHeaderRow, referencesAsLabels, useCustomSorting, nameOpen } = this.state;
        const columnWidth = template.tsvColumnProperty ? template.tsvColumnProperty.length * 263 + 273 : 600;

        const objectProperties = Object.keys(options.objectProperties);
        const datatypeProperties = Object.keys(options.datatypeProperties);
        const fixedOptions = Object.keys(options.columns);
        const dataTypeOptions = datatypeProperties.map(prop => ({
            iri: options.datatypeProperties[prop].iri,
            label: options.datatypeProperties[prop].displayName,
            lang: options.datatypeProperties[prop].lang
        }));
        const objectOptions = objectProperties.map(prop => ({
            iri: options.objectProperties[prop].iri,
            label: options.objectProperties[prop].displayName
        }));
        const labelOptions = fixedOptions
            .map(label => ({
                iri: label,
                disabled: template.tsvColumnProperty && template.tsvColumnProperty.includes(label),
                label: options.columns[label],
                ...(label === "https://grafsync.graphifi.com/schema/tsv/hierarchy"
                    || label === "https://grafsync.graphifi.com/schema/tsv/concepts"
                    || label === "https://grafsync.graphifi.com/schema/tsv/resources") && { lang: true }
            }));
        const additionalOptions = loadInfo.map(info => ({
            iri: info.iri,
            label: info.label && info.label[this.props.defaultLang] ?
                info.label[this.props.defaultLang]
                :
                info.label && info.label["und"] ?
                    info.label["und"]
                    :
                    info.iri,
            ...(info.range && info.range[0] === "http://www.w3.org/1999/02/22-rdf-syntax-ns#langString") && { lang: true }
        }));
        sortByProp(additionalOptions, "label", null, false);

        const optionsList = dataTypeOptions && dataTypeOptions.length > 0 ?
            [
                ...labelOptions,
                {
                    heading: true,
                    label: "Labels, Notes and Deprecation"
                },
                ...dataTypeOptions,
                {
                    heading: true,
                    label: "Relationships"
                },
                ...objectOptions,
                {
                    heading: true,
                    label: "Ontology Properties"
                },
                ...additionalOptions
            ]
            :
            [
                ...labelOptions,
                {
                    heading: true,
                    label: "Relationships"
                },
                ...objectOptions,
                {
                    heading: true,
                    label: "Ontology Properties"
                },
                ...additionalOptions
            ];

        return (
            <div>
                <Typography data-test="build-output" className={classes.mainHeading}>Build Your Output</Typography>
                <div>
                    <div style={{ display: 'flex', width: '100%', justifyContent: 'space-evenly' }}>
                        <div>
                            <LabelledSwitch
                                disabled={isProcessing}
                                dataTest="include-header-row"
                                label="Include Header Row"
                                checked={includeHeaderRow}
                                handleChange={this.handleHeaderRowChange}
                            />
                        </div>
                        <div>
                            <LabelledSwitch
                                disabled={isProcessing}
                                dataTest="references-as-labels"
                                label="Output References as Labels"
                                checked={referencesAsLabels}
                                handleChange={this.handleReferencesAsLabelsChange}
                            />
                        </div>
                        {"sortProperty" in options && options.sortProperty !== undefined && examples && examples.length > 0 &&
                            <div>
                                <LabelledSwitch
                                    disabled={isProcessing}
                                    dataTest="use-custom-sorting"
                                    label="Use Custom Sorting"
                                    checked={useCustomSorting}
                                    handleChange={this.handleCustomSortingChange}
                                />
                            </div>
                        }
                        <div style={{ flex: 1, textAlign: 'right' }}>
                            {allowSave && template.tsvColumnProperty && template.tsvColumnProperty.length > 0 &&
                                <SecondaryButton
                                    disabled={isProcessing}
                                    dataTest="save-as-template"
                                    onClick={this.promptForName}
                                    className={classes.button}>Save as Template</SecondaryButton>
                            }
                        </div>
                    </div>
                </div>
                <div className={classes.selectionPanelContainer}>
                    <div className={classes.selectionPanel}>
                        <div className={classes.columns} style={{ width: columnWidth }}>
                            {
                                template.tsvColumnProperty && template.tsvColumnProperty.map((cp, index) => {
                                    return <div data-test={"column-" + index} key={cp + "-" + index}>
                                        <TSVselectedOption
                                            isDraggable={template.tsvColumnProperty.length > 2}
                                            dragging={index === this.state.draggingIndex}
                                            beginDragging={this.beginDragging}
                                            endDragging={this.endDragging}
                                            index={index}
                                            disabled={isProcessing}
                                            labelProp={options.labelProp}
                                            graph={graph}
                                            showLang={optionsList.some(option => option.iri === cp && option.lang)}
                                            defaultLang={defaultLang}
                                            examples={examples}
                                            moveOption={this.moveOption}
                                            options={optionsList}
                                            lang={template.tsvColumnLanguage[index]}
                                            removeOption={() => this.removeOption(index)}
                                            property={cp} />
                                        {index < template.tsvColumnProperty.length - 1 &&
                                            <TSVoptionSeparator
                                                canDrop={this.state.draggingIndex <= index ?
                                                    index !== this.state.draggingIndex
                                                    :
                                                    index !== this.state.draggingIndex - 1}
                                                moveOption={this.moveOption}
                                                disabled={isProcessing}
                                                index={index}
                                                onClick={() => this.swapColumns(index)} />
                                        }
                                    </div>;
                                })
                            }
                            {(!template.tsvColumnProperty || template.tsvColumnProperty.length < 50) &&
                                <div data-test="add-column-option" style={template.tsvColumnProperty && template.tsvColumnProperty.length > 0 ? { marginLeft: 40, float: 'left' } : {}}>
                                    <TSVoptions
                                        disabled={isProcessing}
                                        template={template}
                                        defaultLang={defaultLang}
                                        options={optionsList}
                                        onAddColumn={this.addColumn} />
                                </div>
                            }
                        </div>
                    </div>
                </div>
                <div className={classes.actions}>
                    <div style={{ float: 'left' }}>
                        <SecondaryButton disabled={isProcessing} dataTest="discard" onClick={onDiscard}>Discard</SecondaryButton>
                    </div>
                    <div style={{ float: 'right' }}>
                        {isProcessing ?
                            <LoadingButton disabled />
                            :
                            <DownloadOptionButton
                                disabled={!template.tsvColumnProperty || template.tsvColumnProperty.length === 0}
                                onSelect={this.onCreate}>Generate</DownloadOptionButton>
                        }
                    </div>
                </div>
                {nameOpen && <RenameTemplateDialog
                    defaultLang={defaultLang}
                    title="Save Template"
                    onExitName={this.exitName}
                    onName={this.save} />}
            </div >
        );
    }
}

TSVbuilder.propTypes = {
    loadInfo: PropTypes.array,
    allowSave: PropTypes.bool.isRequired,
    onDiscard: PropTypes.func.isRequired,
    examples: PropTypes.array,
    isProcessing: PropTypes.bool.isRequired,
    id: PropTypes.string.isRequired,
    templateTarget: PropTypes.string.isRequired,
    onCreate: PropTypes.func.isRequired,
    options: PropTypes.object.isRequired,
    defaultLang: PropTypes.string.isRequired,
    classes: PropTypes.object,
    graph: PropTypes.string.isRequired,
    template: PropTypes.object
};

export default withStyles(styles)(TSVbuilder);